import * as React from 'react';
import { Link, useNavigate } from 'react-router';
import { AccountsError, isMfaChallenge, verifySignup } from '../accounts.ts';
import { useAuth } from '../auth.tsx';
import type { MfaState } from './MfaVerification.tsx';
import { AUTH_LINK, AUTH_SUBMIT, AuthField, AuthNotice, AuthShell, useFragmentToken } from './AuthShell.tsx';

/**
 * The page a confirmation link opens (`/verify#token=…`, ADR 0044 §3). The token is taken out of the address
 * bar at once. Only the password chosen at sign-up confirms the account: the sign-up form takes any address, so
 * the link alone proves nothing about who chose the password. A person who does not know it resets their
 * password, which confirms the account with the new one. A confirmed account then meets a password sign-in's
 * rules: a second-factor step, or single sign-on where its organization requires it.
 */
export function VerifyEmailPage() {
  const token = useFragmentToken();
  const { signedIn } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(
    token ? '' : 'This link is not valid any more. Ask for a new one.',
  );
  const [dead, setDead] = React.useState(!token);
  const [done, setDone] = React.useState('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await verifySignup(token, password);
      if (isMfaChallenge(r)) {
        const next: MfaState = { mfa: r, email: 'your account' };
        navigate('/mfa-verification', { state: next });
        return;
      }
      await signedIn(r);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof AccountsError && err.status === 403) {
        // Confirmed, but no session: this account signs in another way.
        setDone(err.message);
      } else {
        setError(err instanceof AccountsError ? err.message : 'The link could not be checked. Try again.');
        // A wrong password keeps the link good; a dead link needs a new one.
        setDead(
          !(err instanceof AccountsError) ? false : err.status === 400 && !/password/i.test(err.message),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Confirm your email"
      subtitle="Enter the password you chose at sign-up"
      testId="verify-page"
    >
      {done ? (
        <div className="flex flex-col gap-5">
          <AuthNotice kind="ok" testId="verify-done">
            Your email is confirmed. {done}
          </AuthNotice>
          <Link to="/login" className={AUTH_LINK}>
            Sign in
          </Link>
        </div>
      ) : dead ? (
        <div className="flex flex-col gap-5">
          <AuthNotice kind="error" testId="verify-error">
            {error}
          </AuthNotice>
          <Link to="/login" className={AUTH_LINK}>
            Sign in to ask for a new link
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {error && (
            <AuthNotice kind="error" testId="verify-error">
              {error}
            </AuthNotice>
          )}
          <AuthField
            id="verify-password"
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testId="verify-password"
          />
          <button type="submit" disabled={busy} data-testid="verify-submit" className={AUTH_SUBMIT}>
            {busy ? 'Confirming…' : 'Confirm email'}
          </button>
          <Link to="/forgot-password" className={AUTH_LINK}>
            I do not remember it: reset my password
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
